// Authenticated fetch wrapper for the Atlas control-plane API.
//
// Attaches the cached Cognito id_token. On a 401 it triggers background re-login (redirect to the
// Hosted UI, preserving the current URL) — the spec's background-authorization behavior.

import type { AtlasConfig, FetchLike, FetchResponseLike, LocationLike } from "./types";

// The auth surface ApiClient depends on (a subset of Auth) — keeps the coupling explicit + fakeable.
export interface AuthLike {
  getIdToken(): Promise<string | null>;
  login(target?: string): void | Promise<void>;
}

export interface ApiClientDeps {
  config: AtlasConfig;
  auth: AuthLike;
  fetch: FetchLike;
  location: LocationLike;
}

export interface RequestOptions {
  query?: Record<string, string>;
  body?: unknown;
}

// The request surface the higher-level namespaces (preferences/appConfig/workflow) depend on.
export interface ApiRequester {
  request(method: string, path: string, options?: RequestOptions): Promise<any>;
}

export class ApiClient implements ApiRequester {
  config: AtlasConfig;
  auth: AuthLike;
  fetch: FetchLike;
  location: LocationLike;

  constructor(deps: ApiClientDeps) {
    this.config = deps.config;
    this.auth = deps.auth;
    this.fetch = deps.fetch;
    this.location = deps.location;
  }

  async request(method: string, path: string, { query, body }: RequestOptions = {}): Promise<any> {
    const token = await this.auth.getIdToken();
    if (!token) {
      await this.auth.login(this.location.href);
      throw new Error("not authenticated — redirecting to login");
    }
    const url = this.config.apiEndpoint + path + (query ? "?" + new URLSearchParams(query) : "");
    const resp = await this.fetch(url, {
      method,
      headers: { authorization: token, "content-type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (resp.status === 401) {
      await this.auth.login(this.location.href); // session rejected → re-login
      throw new Error("session expired — redirecting to login");
    }
    if (!resp.ok) {
      const detail = await safeJson(resp);
      throw new Error(detail?.message || `HTTP ${resp.status}`);
    }
    return safeJson(resp);
  }
}

async function safeJson(resp: FetchResponseLike): Promise<any> {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}
